import { useEffect, useState } from 'react';
import { Box, IconButton, Modal, Stack, TextField, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CustomText from 'components/common/CustomText/CustomText';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { colors } from 'styles/colors';
import { RawProductWarnings } from 'services/rawProductService';
import { usePressToSave } from './usePressToSave';

interface Props {
  warnings: RawProductWarnings | null;
  open: boolean;
  productName: string;
  isSaving: boolean;
  onClose: () => void;
  onSave: (warnings: RawProductWarnings) => void;
}

// One caution, both languages together: they are the same caution written for
// their own readers, in the same order, so a line removed from one side and
// left on the other is how the two lists stop lining up.
interface WarningDraft {
  en: string;
  ar: string;
}

function toDrafts(warnings: RawProductWarnings | null): WarningDraft[] {
  const en = warnings?.en ?? [];
  const ar = warnings?.ar ?? [];
  const rows = Math.max(en.length, ar.length);

  return Array.from({ length: rows }, (_value, row) => ({
    en: en[row] ?? '',
    ar: ar[row] ?? '',
  }));
}

export default function WarningsModal({
  warnings,
  open,
  productName,
  isSaving,
  onClose,
  onSave,
}: Props) {
  const [drafts, setDrafts] = useState<WarningDraft[]>([]);
  const { saving, press } = usePressToSave(isSaving, open);

  // Opened on what the row holds every time rather than on whatever was left
  // here last: an abandoned edit that came back on the next open would read as a
  // stored caution.
  useEffect(() => {
    if (!open) return;
    setDrafts(toDrafts(warnings));
  }, [open, warnings]);

  const changed = JSON.stringify(drafts) !== JSON.stringify(toDrafts(warnings));

  const patch = (row: number, next: Partial<WarningDraft>) =>
    setDrafts((existing) =>
      existing.map((draft, position) => (position === row ? { ...draft, ...next } : draft)),
    );

  return (
    <Modal open={open} onClose={saving ? undefined : onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
          width: 900,
          maxWidth: '94vw',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <CustomText text="Edit warnings" fontWeight={700} fontSize={18} />
        <Box mt={0.5}>
          <CustomText text={productName} fontSize={13} color={colors.gray} />
        </Box>

        {drafts.length === 0 && (
          <Box mt={2}>
            <CustomText
              text="This product carries no warnings. Saving now leaves it that way."
              fontSize={13}
              color={colors.gray}
            />
          </Box>
        )}

        {drafts.map((draft, row) => (
          <Box
            key={`warning-${row}`}
            mt={1.5}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' },
              columnGap: 1.5,
              rowGap: 1,
              alignItems: 'flex-start',
            }}
          >
            <Stack spacing={0.5}>
              <CustomText
                text={`EN ${row + 1}`}
                fontSize={11}
                fontWeight={600}
                color={colors.gray}
              />
              <TextField
                fullWidth
                size="small"
                multiline
                value={draft.en}
                disabled={saving}
                onChange={(event) => patch(row, { en: event.target.value })}
                placeholder="—"
              />
            </Stack>
            <Stack spacing={0.5}>
              <CustomText
                text={`AR ${row + 1}`}
                fontSize={11}
                fontWeight={600}
                color={colors.gray}
              />
              <TextField
                fullWidth
                size="small"
                multiline
                value={draft.ar}
                disabled={saving}
                onChange={(event) => patch(row, { ar: event.target.value })}
                placeholder="—"
                inputProps={{ dir: 'rtl' }}
              />
            </Stack>
            <Tooltip title="Remove this warning">
              <IconButton
                size="small"
                disabled={saving}
                sx={{ mt: 2.25 }}
                onClick={() =>
                  setDrafts((existing) => existing.filter((_draft, position) => position !== row))
                }
              >
                <DeleteOutlineIcon sx={{ fontSize: 16, color: colors.gray }} />
              </IconButton>
            </Tooltip>
          </Box>
        ))}

        <Box mt={2}>
          <CustomButton
            text="Add warning"
            variant="outlined"
            height={32}
            icon={<AddIcon sx={{ fontSize: 16 }} />}
            disabled={saving}
            onClick={() => setDrafts((existing) => [...existing, { en: '', ar: '' }])}
          />
        </Box>

        <Box mt={1.5}>
          <CustomText
            text="What the pack prints as a caution, one per line, in the order a reader meets them. An empty list says this product carries none."
            fontSize={11}
            color={colors.gray}
          />
        </Box>

        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
          <CustomButton
            text="Cancel"
            variant="outlined"
            height={40}
            disabled={saving}
            onClick={onClose}
          />
          <CustomButton
            text="Save"
            height={40}
            isLoading={saving}
            disabled={!changed}
            onClick={() => press(() => {
              // A caution blank in both languages is one the reviewer emptied
              // rather than one they wrote.
              const kept = drafts.filter((draft) => draft.en.trim() || draft.ar.trim());
              onSave({
                en: kept.map((draft) => draft.en.trim()),
                ar: kept.map((draft) => draft.ar.trim()),
              });
            })}
          />
        </Stack>
      </Box>
    </Modal>
  );
}
